// ── PII sanitising ───────────────────────────────────────────────────────────
// The last thing an event passes before it reaches the data layer. The schema decides which
// parameters may exist; this decides what may be inside them.
//
// Two independent checks, because either one alone has a hole. Names are checked because a key
// called "customerEmail" is a declaration of intent whatever it happens to hold right now. Values
// are checked because the field a customer typed into is rarely the field named after them — an
// e-mail pasted into a project title arrives under the key "page_title", and only a look at the
// value catches it.

/** What replaces a match inside text we keep (a page title stays useful with a hole in it). */
export const REDACTED = "[fjernet]";

// ── Patterns ─────────────────────────────────────────────────────────────────

const EMAIL_SOURCE = "[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}";

// A phone number is only recognisable by its shape: either an international prefix, or digits laid
// out in groups. A bare run of digits is deliberately NOT part of this — an order number and a
// Norwegian mobile number are both eight digits, and stripping every id that happens to look like a
// phone number would quietly empty lead_id and transaction_id. Bare runs are handled below, where
// the key tells us whether the field is an identifier.
const PHONE_SOURCE =
  "(?:(?:\\+|00)\\d{1,3}[\\s.\\-()]*)?\\b\\d{2,3}(?:[\\s.\\-]\\d{2,3}){2,3}\\b" +
  "|(?:\\+|00)\\d{1,3}[\\s.\\-()]*\\d{6,12}\\b";

/** Eight digits on their own — a Norwegian number, unless the key says the field holds an id. */
const BARE_PHONE_SOURCE = "\\b\\d{8}\\b";

const EMAIL = new RegExp(EMAIL_SOURCE, "i");
const PHONE = new RegExp(PHONE_SOURCE);
const BARE_PHONE = new RegExp(BARE_PHONE_SOURCE);

// ── Key denylist ─────────────────────────────────────────────────────────────

/**
 * Keys that are dropped on an exact match. These are the short or ambiguous ones: matched as
 * substrings they would take innocent keys with them ("capacity" contains "city", "liquid" contains
 * "uid"), so they only count when they are the whole key.
 */
export const PII_KEYS: readonly string[] = [
  "ip",
  "ip_address",
  "client_ip",
  "remote_addr",
  "uid",
  "oid",
  "sub",
  "upn",
  "user_id",
  "userid",
  "ssn",
  "dob",
  "gender",
  "city",
  "zip",
  "q",
];

/**
 * Fragments that condemn a key wherever they appear in it. Anything holding a person, a place, a
 * credential or free text — free text most of all, because it is the field with no shape at all and
 * therefore the one no value check can clear.
 */
export const PII_KEY_TOKENS: readonly string[] = [
  // person
  "email",
  "e_mail",
  "epost",
  "e_post",
  "mail",
  "phone",
  "telefon",
  "tlf",
  "mobil",
  "msisdn",
  "name",
  "navn",
  "birth",
  "fodsel",
  "personnr",
  "customer",
  "kunde",
  "contact",
  "kontakt",
  "recipient",
  "mottaker",
  "sender",
  "avsender",
  // place
  "address",
  "adresse",
  "postnr",
  "postal",
  "postcode",
  "poststed",
  // credential
  "token",
  "secret",
  "password",
  "passord",
  "credential",
  "bearer",
  "apikey",
  "api_key",
  "signature",
  "auth",
  // money and registers
  "orgnr",
  "kontonr",
  "iban",
  "card",
  // free text
  "comment",
  "kommentar",
  "message",
  "melding",
  "note",
  "notat",
  "description",
  "beskrivelse",
  "fritekst",
  "freetext",
  "free_text",
  "remark",
  "query",
  "search_term",
  "searchterm",
];

/**
 * Keys that survive the denylist because they are known to hold catalogue or session metadata:
 * <c>item_name</c> is a door model, <c>auth_state</c> is "guest" or "authenticated". They are exempt
 * from the NAME check only — their values are still scanned, so an e-mail that somehow ends up in an
 * item name is still dropped.
 */
export const SAFE_KEYS: readonly string[] = ["item_name", "item_list_name", "auth_state"];

const SAFE = new Set(SAFE_KEYS);
const DENIED = new Set(PII_KEYS);

/** Query parameters a page URL may keep. Everything else goes, including our own ?token=. */
export const QUERY_ALLOWLIST: readonly string[] = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_id",
  "gclid",
  "gbraid",
  "wbraid",
  "dclid",
  "srsltid",
  "fbclid",
  "ttclid",
  "msclkid",
];

const ALLOWED_QUERY = new Set(QUERY_ALLOWLIST);

/** Depth beyond which a value is dropped rather than walked — also what stops a cyclic object. */
const MAX_DEPTH = 6;

// ── Checks ───────────────────────────────────────────────────────────────────

/** Lower-cases and folds separators, so "e-post", "E_Post" and "ePost" are all the same key. */
function normaliseKey(key: string): string {
  return key.toLowerCase().replace(/[\s.-]+/g, "_");
}

/** True if the key alone is reason enough to drop the field. */
export function isPiiKey(key: string): boolean {
  const normalised = normaliseKey(key);
  if (SAFE.has(normalised)) return false;
  if (DENIED.has(normalised)) return true;
  return PII_KEY_TOKENS.some((token) => normalised.includes(token));
}

/** Machine identifiers ("lead_id", "item_id"). Their digits are not phone numbers. */
function isIdentifierKey(key: string): boolean {
  const normalised = normaliseKey(key);
  return normalised === "id" || normalised.endsWith("_id");
}

/**
 * True if the value looks like a person, whatever it is filed under. The key is passed in only to
 * decide whether a bare run of digits counts as a phone number.
 */
export function containsPii(key: string, value: string): boolean {
  if (EMAIL.test(value)) return true;
  if (PHONE.test(value)) return true;
  return !isIdentifierKey(key) && BARE_PHONE.test(value);
}

// ── Text and URLs ────────────────────────────────────────────────────────────

/**
 * Redacts rather than drops. A page title is the one string where losing everything costs more than
 * it saves: "Prosjekt for ola@example.com" is still worth reporting as "Prosjekt for [fjernet]".
 * Everywhere else a matching value is removed outright.
 */
export function sanitizeText(raw: string): string {
  return raw
    .replace(new RegExp(EMAIL_SOURCE, "gi"), REDACTED)
    .replace(new RegExp(PHONE_SOURCE, "g"), REDACTED)
    .replace(/\s{2,}/g, " ")
    .trim();
}

function parseUrl(raw: string): URL | null {
  try {
    return new URL(raw);
  } catch {
    // Relative — resolve against the current document if there is one.
  }
  try {
    if (typeof window === "undefined") return null;
    return new URL(raw, window.location.origin);
  } catch {
    return null;
  }
}

/**
 * Origin, path, and nothing else but allow-listed campaign parameters.
 * <p>The fragment goes unconditionally: it never reaches a server, so nobody reviews what ends up in
 * it. The query is emptied to an allowlist rather than trimmed by a denylist, because the parameter
 * that leaks is always the one nobody thought of — the sales-rep <c>?token=</c> among them.</p>
 */
export function sanitizeUrl(raw: string): string {
  const url = parseUrl(raw);
  if (!url) return "";

  const kept = new URLSearchParams();
  for (const [key, value] of url.searchParams) {
    if (!ALLOWED_QUERY.has(key.toLowerCase())) continue;
    if (containsPii(key, value)) continue;
    kept.append(key, value);
  }

  const query = kept.toString();
  return `${url.origin}${url.pathname}${query ? `?${query}` : ""}`;
}

/**
 * Same treatment, except that a foreign origin loses its query entirely — we have no say in what a
 * search engine or a partner site puts in its own URLs.
 */
export function sanitizeReferrer(raw: string): string {
  if (!raw) return "";

  const url = parseUrl(raw);
  if (!url) return "";

  const ourOrigin = typeof window === "undefined" ? null : window.location.origin;
  if (ourOrigin !== null && url.origin === ourOrigin) return sanitizeUrl(raw);

  return `${url.origin}${url.pathname}`;
}

// ── Deep sanitising ──────────────────────────────────────────────────────────

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Sanitises one value in the context of the key it sits under. Returns undefined to drop it. */
function sanitizeValue(key: string, value: unknown, depth: number): unknown {
  if (depth > MAX_DEPTH) return undefined;

  if (typeof value === "string") return containsPii(key, value) ? undefined : value;

  // Nothing else survives the trip into the data layer intact, and a function or a symbol in an
  // event is a mistake rather than data.
  if (typeof value === "function" || typeof value === "symbol") return undefined;

  if (Array.isArray(value)) {
    // The element keeps its parent's key: an entry in `items` is judged as an item, not as "0".
    return value
      .map((entry) => sanitizeValue(key, entry, depth + 1))
      .filter((entry) => entry !== undefined);
  }

  if (isPlainObject(value)) return walk(value, depth + 1);

  return value;
}

function walk(input: Record<string, unknown>, depth: number): Record<string, unknown> {
  const output: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(input)) {
    if (value === undefined) continue;
    if (isPiiKey(key)) continue;

    const cleaned = sanitizeValue(key, value, depth);
    if (cleaned !== undefined) output[key] = cleaned;
  }

  return output;
}

/**
 * Walks an event (or any object bound for the data layer) and removes every field that names a
 * person or holds one. Nested objects and arrays are walked too — <c>items</c> is exactly the place
 * a stray name would sit unnoticed.
 *
 * <p>Returns a copy; the caller's object is untouched. The return type is a convenient lie — keys
 * really can be missing afterwards — which is why the schema check runs after this, not before: it
 * is what notices that a required parameter was carried off.</p>
 */
export function deepSanitize<T>(value: T): T {
  if (!isPlainObject(value)) return value;
  return walk(value, 0) as unknown as T;
}
